import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, DollarSign, CalendarDays, Eye, EyeOff, Mail, MapPin, Phone, Sun, User, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrgId } from "../api/org";
import { PanelCanvas } from "../components/PanelCanvas";
import { Alert, Badge, Button, Card, Empty, PageHead, Select, Spinner, useToast } from "../components/ui";
import { date, money, num, titleCase } from "../lib/format";
import { leadName, leadState } from "./Designs";

function useSiteImage(url: string | undefined) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    rawFetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => !cancelled && setImage(img);
        img.src = objectUrl;
      })
      .catch((e) => setError(errorMessage(e)));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return { image, error };
}

function Metric({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="card stat">
      <div className="label">
        {icon} {label}
      </div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export function DesignDetail() {
  const { designId = "" } = useParams();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const toast = useToast();
  const [showAvailable, setShowAvailable] = useState(true);

  const design = useQuery({
    queryKey: ["design", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}", { params: { path: { design_id: designId } } })),
  });
  const project = useQuery({
    queryKey: ["project", design.data?.project_id],
    enabled: !!design.data,
    queryFn: () => unwrap(api.GET("/v1/projects/{project_id}", { params: { path: { project_id: design.data!.project_id } } })),
  });
  const version = design.data?.current_version_number ?? null;
  const site = useQuery({
    queryKey: ["site", designId, version],
    enabled: !!version,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/versions/{version_number}/site", {
          params: { path: { design_id: designId, version_number: version! } },
        }),
      ),
  });
  const quotes = useQuery({
    queryKey: ["quotes", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/quotes", { params: { path: { design_id: designId }, query: { limit: 1 } } })),
  });
  const versionDoc = useQuery({
    queryKey: ["version", designId, version],
    enabled: !!version,
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/versions/{version_number}", {
          params: { path: { design_id: designId, version_number: version! } },
        }),
      ),
  });
  // The lead (website quote) of this design, from the leads list.
  const lead = useQuery({
    queryKey: ["lead-of", orgId, designId],
    queryFn: async () => {
      const page = await unwrap(api.GET("/v1/orgs/{org_id}/leads", { params: { path: { org_id: orgId }, query: { limit: 100 } } }));
      return page.items.find((i) => i.design_id === designId) ?? null;
    },
  });
  const updateStatus = useMutation({
    mutationFn: (status: S["DesignStatus"]) =>
      unwrap(api.PATCH("/v1/designs/{design_id}", { params: { path: { design_id: designId } }, body: { status } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["design", designId] });
      qc.invalidateQueries({ queryKey: ["leads-infinite"] });
      toast("Status updated");
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const { image, error: imageError } = useSiteImage(site.data?.site_image?.url);

  if (design.isLoading) return <Spinner />;
  if (design.error || !design.data) return <Alert tone="danger">{errorMessage(design.error)}</Alert>;

  const d = design.data;
  const quote = quotes.data?.items[0]?.output;
  const summary = quote?.system_summary;
  const fin = quote?.financials;
  const currency = quote?.currency ?? "USD";
  const layoutSummary = versionDoc.data?.stages.find((s) => s.stage === "layout")?.summary;
  const kw = summary?.system_size_kw ?? layoutSummary?.kw_dc ?? lead.data?.kw_dc;
  const panels = summary?.selected_panel_count ?? layoutSummary?.module_count ?? lead.data?.panel_count;
  const production = summary?.est_annual_production_kwh ?? layoutSummary?.annual_kwh;
  const address = lead.data?.address || project.data?.address || project.data?.normalized_address || d.name;
  const state = lead.data ? leadState(lead.data) : null;
  const contact = lead.data?.contact;

  return (
    <>
      <PageHead
        crumbs={
          <>
            <Link to="/designs">
              <ArrowLeft size={12} style={{ verticalAlign: -1 }} /> Designs &amp; leads
            </Link>
          </>
        }
        title={address}
        description={
          <span className="row" style={{ gap: 8 }}>
            {state && <Badge tone={state.tone}>{state.label}</Badge>}
            <span>
              {d.name} · v{version ?? "—"} · created {date(d.created_at, true)}
            </span>
          </span>
        }
        actions={
          <div style={{ width: 160 }}>
            <Select
              value={d.status}
              onChange={(s) => updateStatus.mutate(s)}
              options={(["draft", "proposed", "sold", "archived"] as const).map((s) => ({ value: s, label: `Status: ${titleCase(s)}` }))}
            />
          </div>
        }
      />
      <div className="split-wide">
        <div className="stack">
          <Card pad={false}>
            {site.isLoading || (site.data?.site_image && !image && !imageError) ? (
              <div className="design-canvas skeleton" />
            ) : site.data?.site_image && image ? (
              <div className="design-canvas">
                <PanelCanvas site={site.data} image={image} showAvailable={showAvailable} />
                <div className="overlay-tools">
                  <Button size="sm" icon={showAvailable ? <EyeOff /> : <Eye />} onClick={() => setShowAvailable((v) => !v)}>
                    {showAvailable ? "Hide" : "Show"} other positions
                  </Button>
                </div>
              </div>
            ) : (
              <Empty icon={<Sun />} title="No roof design yet">
                {imageError || (site.error ? errorMessage(site.error) : "The design is still being built, or its imagery is missing.")}
              </Empty>
            )}
          </Card>
          <div className="grid-4 stats-tight">
            <Metric icon={<Zap size={14} />} label="System size" value={<>{num(kw, 1)}<small>kW</small></>} sub={summary ? `${summary.panel_watts} W modules` : undefined} />
            <Metric icon={<Sun size={14} />} label="Panels" value={num(panels)} sub={summary ? `${summary.min_panels}–${summary.max_panels} fit` : site.data ? `${site.data.panels.length} positions` : undefined} />
            <Metric icon={<CalendarDays size={14} />} label="Production" value={<>{num(production)}<small>kWh/yr</small></>} sub={summary ? `${num(summary.est_offset_percentage)} % offset` : "DC, before losses"} />
            <Metric icon={<DollarSign size={14} />} label="Price" value={fin ? money(fin.full_system_price, currency) : "—"} sub={fin ? `${money(fin.net_cost, currency)} after incentives` : "Not priced yet"} />
          </div>
        </div>
        <div className="stack">
          <Card title="Lead">
            {contact ? (
              <div className="stack" style={{ gap: 10 }}>
                <div className="row">
                  <User size={16} className="faint" /> <b>{leadName(lead.data!) ?? "—"}</b>
                </div>
                {contact.email && (
                  <div className="row">
                    <Mail size={16} className="faint" /> <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </div>
                )}
                {contact.phone && (
                  <div className="row">
                    <Phone size={16} className="faint" /> <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                  </div>
                )}
                <div className="row">
                  <MapPin size={16} className="faint" /> <span className="muted">{address}</span>
                </div>
                {lead.data?.roof_type && <div className="small muted">Roof type: {titleCase(lead.data.roof_type.replace(/-/g, " "))}</div>}
              </div>
            ) : (
              <p className="muted">{lead.data?.source === "website" ? "The visitor has not left their details yet." : "Designed in the dashboard — no website lead."}</p>
            )}
          </Card>
          <Card title="Quote" description={quote ? `As of ${date(quote.as_of)} · ${titleCase(quote.financing)}` : undefined}>
            {fin && summary ? (
              <dl className="kv">
                <dt>System price</dt>
                <dd>{money(fin.full_system_price, currency)}</dd>
                <dt>Incentives</dt>
                <dd>−{money(fin.incentives, currency)}</dd>
                <dt>Net cost</dt>
                <dd>{money(fin.net_cost, currency)}</dd>
                <dt>Price per watt</dt>
                <dd>{money(fin.price_per_watt, currency, 2)}</dd>
                <dt>Bill today → with solar</dt>
                <dd>
                  {money(fin.monthly_bill_old, currency)} → {money(fin.monthly_bill_new, currency)}/mo
                </dd>
                <dt>Year-1 savings</dt>
                <dd>{money(fin.annual_savings_year1, currency)}</dd>
                <dt>Payback</dt>
                <dd>{fin.payback_years != null ? `${num(fin.payback_years, 1)} years` : "—"}</dd>
                <dt>Lifetime savings</dt>
                <dd>{money(fin.lifetime_savings, currency)}</dd>
                <dt>Equipment</dt>
                <dd className="ellipsis" title={summary.panel_model}>
                  {summary.selected_panel_count} × {summary.panel_model}
                </dd>
                <dt>Inverter</dt>
                <dd className="ellipsis">
                  {summary.inverter_count} × {summary.inverter_model}
                </dd>
              </dl>
            ) : (
              <p className="muted">No priced quote yet. Website quotes are priced when the visitor finishes the calculator.</p>
            )}
          </Card>
          {versionDoc.data && (
            <Card title="Pipeline" description={`Version ${versionDoc.data.number} · ${titleCase(versionDoc.data.origin)}`}>
              <div className="pipeline-flow">
                {versionDoc.data.stages.map((s) => (
                  <span key={s.stage} className={`node${s.state === "pending" ? " off" : ""}`} title={s.state}>
                    {titleCase(s.stage)}
                    {s.state === "failed" ? " ✕" : s.state === "manual" ? " ✎" : ""}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
